import {
  select, call, put, all, takeEvery, takeLatest, take, race
} from 'redux-saga/effects';
import { validateSection } from 'helpers/validation';
import { selectValidUSPassport, selectRequiredDocumentsSF852013 } from 'selectors/misc';
import { SagaIterator } from 'redux-saga';
import { RootActionTypes } from '@redux/modules/actionTypes';
import { buildFormError } from 'helpers/formErrors';
import { ValidateErrorContext } from 'forms/shared/types/validatejs';
import * as ActionTypes from './actionTypes';

import * as selectors from './selectors';
import {
  updateSubsectionSuccess,
  validateForm,
  UpdateSubsectionRequestAction,
  validateFormSuccess,
  validateFormFailure,
  SubsectionValidation,
} from './actions';
import { FormState, FormStateSubsectionsKeys, FormError } from './types';
import { FetchFormSuccessAction } from '../api/actions';
import { rootSelectors } from '../selectors';

const ignoreAttributes = ['initialValidateFormCompleted', 'currentScrollValue'];

export function* onFormSuccess(action: FetchFormSuccessAction): SagaIterator {
  try {
    yield put(validateForm());
  } catch (e) {
    console.warn('failed to set form data', e);
  }
}

/**
 * ValidateConfig has an error object formatter, however, it can overwrite child
 * components depending on how it is configured. This preserves it while
 * temporarily providing the context inside the state. Once the refactoring is
 * done, we can remove context.
 */
const errorMap = (errors: boolean | ValidateErrorContext[] = []): FormError[] => {
  if (typeof errors === 'boolean') { return []; }
  if (errors === undefined) { return []; }

  return errors.map((e) => buildFormError(e));
};

/**
 * oldErrorMap allows existing functionality to continue to operate, as is. Once
 * the refactoring is done, we can remove this.
 */
const oldErrorMap = (errors: boolean | ValidateErrorContext[] = []): string[] => {
  if (typeof errors === 'boolean') { return []; }
  if (errors === undefined) { return []; }

  return errors.map((e: any) => `${e.attribute}.${e.validator}.${e.error.toString()}`);
};

export function* handleValidateForm(): SagaIterator {
  try {
    const formType = yield select(rootSelectors.application.getFormType);
    const applicantBirthdate = yield select(rootSelectors.application.getApplicantBirthdate);
    const applicantSex = yield select(rootSelectors.application.getApplicantSex);
    const peopleWhoKnowYou = yield select(rootSelectors.application.getPeopleWhoKnowYouWell);
    const maritalStatus = yield select(rootSelectors.application.getMaritalStatus);
    const relativesList = yield select(rootSelectors.application.getRelativesList);
    const applicantSSN = yield select(rootSelectors.application.getApplicantSSN);
    const maritalSSN = yield select(rootSelectors.application.getMaritalSSN);
    const cohabitantsList = yield select(rootSelectors.application.getCohabitantsList);
    const militaryHasServed = yield select(rootSelectors.application.getMilitaryHistoryHasServed);
    const hasValidUSPassport = yield select(selectValidUSPassport);
    const requiredDocumentsEmptySF852013 = yield select(selectRequiredDocumentsSF852013);
    const relationshipsOldestBirthdate = yield select(
      rootSelectors.form.getRelationshipsOldestBirthdate
    );
    const relationshipsYoungestBirthdate = yield select(
      rootSelectors.form.getRelationshipsYoungestBirthdate
    );
    // pass any x-section form data required to validate here
    const validationOptions = {
      applicantBirthdate,
      applicantSex,
      peopleWhoKnowYou,
      relationshipsOldestBirthdate,
      relationshipsYoungestBirthdate,
      maritalStatus,
      relativesList,
      cohabitantsList,
      maritalSSN,
      applicantSSN,
      militaryHasServed,
      ...hasValidUSPassport,
      ...requiredDocumentsEmptySF852013,
    };

    const form: FormState = yield select(selectors.getForm);
    const newFormState: FormState = {
      ...form,
    };

    Object.keys(form).forEach((subsectionKey) => {
      if (ignoreAttributes.includes(subsectionKey)) { return; }
      const key: FormStateSubsectionsKeys = subsectionKey as FormStateSubsectionsKeys;

      const sectionData = form[key]?.data ?? {};
      const rawErrors = validateSection(
        {
          subsectionKey,
          data: sectionData,
          options: validationOptions,
        },
        formType
      );
      const formErrors = errorMap(rawErrors);
      const oldErrors = oldErrorMap(rawErrors);

      const targetSubsection: FormState[typeof key] = {
        ...newFormState[key],
        errors: oldErrors,
        complete: oldErrors.length === 0 || rawErrors === true,
        formErrors,
      };

      // @ts-ignore - FormState has many individual types and there are
      // overlapping conflicts with the individual models. While the below
      // statement does work, I'm not sure how to make this 100% type-safe.
      newFormState[key] = targetSubsection;
    });

    yield put(validateFormSuccess(newFormState));
  } catch (err) {
    console.error('ValidateForm failed: ', err);
    yield put(validateFormFailure());
  }
}

export function* handleSubsectionUpdateRequest(
  action: UpdateSubsectionRequestAction
): SagaIterator {
  try {
    const { subsectionKey, data, validateSubsectionKeys } = action.payload;
    const formType = yield select(rootSelectors.application.getFormType);
    const applicantBirthdate = yield select(rootSelectors.application.getApplicantBirthdate);
    const applicantSex = yield select(rootSelectors.application.getApplicantSex);
    const peopleWhoKnowYou = yield select(rootSelectors.application.getPeopleWhoKnowYouWell);
    const maritalStatus = yield select(rootSelectors.application.getMaritalStatus);
    const relativesList = yield select(rootSelectors.application.getRelativesList);
    const applicantSSN = yield select(rootSelectors.application.getApplicantSSN);
    const maritalSSN = yield select(rootSelectors.application.getMaritalSSN);
    const cohabitantsList = yield select(rootSelectors.application.getCohabitantsList);
    const hasValidUSPassport = yield select(selectValidUSPassport);
    const requiredDocumentsEmptySF852013 = yield select(selectRequiredDocumentsSF852013);
    const relationshipsOldestBirthdate = yield select(
      rootSelectors.form.getRelationshipsOldestBirthdate
    );
    const relationshipsYoungestBirthdate = yield select(
      rootSelectors.form.getRelationshipsYoungestBirthdate
    );
    const militaryHasServed = yield select(rootSelectors.application.getMilitaryHistoryHasServed);
    const validationOptions = {
      applicantBirthdate,
      applicantSex,
      peopleWhoKnowYou,
      relationshipsOldestBirthdate,
      relationshipsYoungestBirthdate,
      maritalStatus,
      relativesList,
      cohabitantsList,
      maritalSSN,
      applicantSSN,
      militaryHasServed,
      ...hasValidUSPassport,
      ...requiredDocumentsEmptySF852013,
    };

    // Handles validations of subsections
    const validations: SubsectionValidation[] = [];
    for (const section of new Set(validateSubsectionKeys)) {
      const form: FormState = yield select(selectors.getForm);
      const key: FormStateSubsectionsKeys = section as FormStateSubsectionsKeys;

      const rawErrors = validateSection(
        {
          subsectionKey: key,
          data: key === subsectionKey ? data : form[key]?.data ?? {},
          options: validationOptions,
        },
        formType
      );
      const oldErrors = oldErrorMap(rawErrors);

      validations.push({
        key,
        errors: oldErrors,
        complete: oldErrors.length === 0 || rawErrors === true,
        formErrors: errorMap(rawErrors),
      });
    }

    yield put(updateSubsectionSuccess(subsectionKey, data, validations));
  } catch (err) {
    console.error('HandleSubsectionUpdate failed: ', err);
    // do nothing?
  }
}

export function* watchFormActions() {
  yield all([
    takeLatest(RootActionTypes.api.FETCH_FORM_SUCCESS, onFormSuccess),
    takeEvery(ActionTypes.UPDATE_SUBSECTION_REQUEST, handleSubsectionUpdateRequest),
    takeLatest(ActionTypes.VALIDATE_FORM, handleValidateForm),
  ]);
}

export function* onLoggedIn() {
  // This will keep watchFormActions active as long as we're logged in.
  yield race({
    loggedIn: call(watchFormActions),
    logout: take(RootActionTypes.authentication.LOGGED_OFF),
  });
}

export default function* rootSaga() {
  yield takeLatest(RootActionTypes.authentication.LOGGED_IN, onLoggedIn);
}
